import {
  GetMeCommand,
  GetNotificationCommand,
  GetOrganizationCommand,
  type Notification,
  type Organization,
  type User,
} from '@perfectpost/perfect-post-common';
import {useQuery} from '@tanstack/react-query';

import {sendOrThrow} from '../../../services/sendOrThrow';
import {sessionKeys} from '../keys';
import {useAuthenticatedUserQuery} from './useAuthenticatedUserQuery';

export interface MeQueryResult {
  me: User;
  valid: boolean;
  organizations: Organization[];
  notifications: Notification;
}

/**
 * L'utilisateur connecté, ses organisations et ses notifications.
 *
 * Remplace le thunk `getMe` de `src/store/mainslice.ts`, dispatché depuis une
 * quinzaine d'endroits — après chaque action susceptible de modifier le profil,
 * chacun devant penser à le faire. Ces appels deviennent des invalidations,
 * portées par les mutations elles-mêmes.
 *
 * Les trois requêtes restent groupées comme dans le thunk : les vues qui lisent
 * l'utilisateur lisent presque toujours aussi ses organisations, et les séparer
 * multiplierait les états de chargement sans rien gagner.
 */
/**
 * Options partagées : `useMeQuery` les monte comme requête, `useFetchMe` les
 * passe à `ensureQueryData` pour lire la valeur en dehors du rendu. Sans ça, une
 * lecture impérative sur une clé jamais montée n'aurait aucun moyen de charger.
 *
 * Pas de `enabled` ici : `ensureQueryData` l'ignorerait de toute façon, et
 * `useFetchMe` n'est appelé que depuis des parcours connectés. La porte est
 * posée par `useMeQuery` seul.
 */
export const meQueryOptions = () => ({
  queryKey: sessionKeys.me(),
  queryFn: async (): Promise<MeQueryResult> => {
    const me = await sendOrThrow(new GetMeCommand());
    const [organizations, notifications] = await Promise.all([
      sendOrThrow(new GetOrganizationCommand()),
      sendOrThrow(new GetNotificationCommand()),
    ]);
    return {me, valid: me.lnAccessTokenValid ?? false, organizations, notifications};
  },
});

/**
 * Ne part que s'il y a un utilisateur Cognito connu d'Amplify : monté sous
 * `SessionProvider`, donc sur les routes publiques aussi, où la requête partirait
 * non signée (cf. `useAuthenticatedUserQuery`). Tant que la réponse n'est pas
 * connue (`undefined`), on n'envoie rien non plus.
 */
export function useMeQuery() {
  const {data: authenticated} = useAuthenticatedUserQuery();
  return useQuery({...meQueryOptions(), enabled: authenticated === true});
}
